import React, {useState, useMemo, useCallback} from 'react';
import {Text, Icon} from '@/ui-kits';
import {palette} from '@/theme/Palette';
import {computeDiff, filterChanges, DiffNode, DiffType} from '@/utils/json-diff/diffAlgorithm';

interface JsonDiffViewerProps {
  oldValue: any;
  newValue: any;
  showOldValue?: boolean; // Show full old value (default: true)
  showOnlyChanges?: boolean; // For new value: show only changes (default: true)
  showParsedView?: boolean; // Show parsed JSON view or plain JSON (default: true)
}

interface TreeNodeProps {
  node: DiffNode;
  path: string;
  level: number;
  isOldValue?: boolean;
}

/**
 * Check if value is a plain object
 */
const isPlainObject = (value: any): boolean => {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && Object.prototype.toString.call(value) === '[object Object]';
};

/**
 * Format value for display
 */
const formatValue = (value: any): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === 'object') return `Object(${Object.keys(value).length})`;
  return String(value);
};

/**
 * Format key for display
 */
const formatKey = (key: string | number): string => {
  return String(key);
};

/**
 * Get change indicator symbol
 */
const getChangeIndicator = (type: DiffType): string => {
  switch (type) {
    case 'added':
      return '+';
    case 'removed':
      return '-';
    case 'modified':
      return '~';
    default:
      return ' ';
  }
};

/**
 * Check if node has nested children
 */
const hasNestedChildren = (node: DiffNode): boolean => {
  if (node.children && node.children.length > 0) return true;
  
  const value = node.oldValue !== undefined ? node.oldValue : node.newValue;
  if (value === null || value === undefined) return false;
  if (typeof value === 'object' && !Array.isArray(value)) return true;
  if (Array.isArray(value)) return true;
  return false;
};

/**
 * Recursive tree node component
 */
const TreeNode: React.FC<TreeNodeProps> = ({node, path, level, isOldValue = false}) => {
  const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first 2 levels
  
  const hasChildren = hasNestedChildren(node);
  const showValue = !hasChildren || (hasChildren && isExpanded);
  
  const displayValue = isOldValue ? node.oldValue : node.newValue;
  const changeType = isOldValue && node.type === 'modified' ? 'removed' : node.type;
  const indicator = getChangeIndicator(changeType);

  const toggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  const currentPath = `${path}.${String(node.key)}`;

  // For old value view, show removed, modified, and unchanged
  // For new value view, show added and modified (filtered by parent component)
  // This filtering is mainly handled by the filterChanges function, but we keep this for safety

  return (
    <div className="select-none" style={{paddingLeft: `${level * 16}px`}}>
      <div 
        className="flex items-start gap-1 py-0.5 hover:bg-gray-50 rounded"
        style={{color: palette.ui_input_border_dark}}
      >
        {/* Expand/Collapse button */}
        {hasChildren && (
          <button
            onClick={toggleExpand}
            className="flex items-center justify-center w-4 h-4 mt-0.5 hover:bg-gray-200 rounded flex-shrink-0"
            style={{color: palette.ui_input_border_dark}}
          >
            <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
              ▶
            </span>
          </button>
        )}
        {!hasChildren && <div className="w-4" />}

        {/* Change indicator - no color highlighting */}
        <span className="font-mono text-xs font-bold mr-1 flex-shrink-0" style={{color: palette.ui_input_border_dark}}>
          {indicator}
        </span>

        {/* Key */}
        <span className="font-FigtreeSemiBold text-sm mr-1" style={{color: palette.ui_input_border_dark}}>
          {formatKey(node.key)}:
        </span>

        {/* Value or summary */}
        {showValue && (
          <>
            {hasChildren ? (
              <span className="text-xs italic" style={{color: palette.ui_input_border}}>
                {Array.isArray(displayValue) 
                  ? `Array(${displayValue.length})`
                  : `Object(${Object.keys(displayValue || {}).length})`}
              </span>
            ) : (
              <span className="text-sm font-mono" style={{color: palette.ui_input_border_dark}}>
                {formatValue(displayValue)}
              </span>
            )}
          </>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children && node.children.length > 0 ? (
            node.children.map((child, index) => (
              <TreeNode
                key={`${currentPath}.${index}`}
                node={child}
                path={currentPath}
                level={level + 1}
                isOldValue={isOldValue}
              />
            ))
          ) : (
            // Render the object/array structure
            <ObjectArrayRenderer
              value={displayValue}
              path={currentPath}
              level={level + 1}
              isOldValue={isOldValue}
              parentType={changeType}
            />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Render object or array structure when children are not pre-computed
 */
const ObjectArrayRenderer: React.FC<{
  value: any;
  path: string;
  level: number;
  isOldValue: boolean;
  parentType: DiffType;
}> = ({value, path, level, isOldValue, parentType}) => {
  if (!value || typeof value !== 'object') return null;

  if (Array.isArray(value)) {
    return (
      <>
        {value.map((item, index) => (
          <TreeNode
            key={`${path}[${index}]`}
            node={{
              key: index,
              type: parentType === 'removed' ? 'removed' : parentType === 'added' ? 'added' : 'unchanged',
              oldValue: isOldValue ? item : undefined,
              newValue: !isOldValue ? item : undefined,
            }}
            path={`${path}[${index}]`}
            level={level}
            isOldValue={isOldValue}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {Object.entries(value).map(([key, val]) => (
        <TreeNode
          key={`${path}.${key}`}
          node={{
            key,
            type: parentType === 'removed' ? 'removed' : parentType === 'added' ? 'added' : 'unchanged',
            oldValue: isOldValue ? val : undefined,
            newValue: !isOldValue ? val : undefined,
          }}
          path={`${path}.${key}`}
          level={level}
          isOldValue={isOldValue}
        />
      ))}
    </>
  );
};

/**
 * Format value as plain JSON string
 */
const formatPlainJson = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Main JSON Diff Viewer Component
 */
export const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
  oldValue,
  newValue,
  showOldValue = true,
  showOnlyChanges = true,
  showParsedView: initialShowParsedView = false, // Default to plain JSON
}) => {
  const [showOldParsedView, setShowOldParsedView] = useState(false); // Default to plain JSON for old value
  const [showNewDiffView, setShowNewDiffView] = useState(false); // Default to plain JSON (full new value), toggle shows diff

  const parseValue = useCallback((val: any): any => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  }, []);

  const oldParsed = useMemo(() => parseValue(oldValue), [oldValue, parseValue]);
  const newParsed = useMemo(() => parseValue(newValue), [newValue, parseValue]);

  const fullDiff = useMemo(() => computeDiff(oldParsed, newParsed), [oldParsed, newParsed]);
  const changesOnlyDiff = useMemo(() => filterChanges(fullDiff), [fullDiff]);

  const oldDiff = useMemo(() => {
    if (!showOldValue) return null;
    // For old value, we always show full diff, but structure it properly
    return fullDiff;
  }, [fullDiff, showOldValue]);

  const newDiff = useMemo(() => {
    return showOnlyChanges ? changesOnlyDiff : fullDiff;
  }, [fullDiff, changesOnlyDiff, showOnlyChanges]);

  // Always render - we always show new value section, and optionally old value

  return (
    <div className="flex flex-col gap-4">
      {/* Old Value View */}
      {showOldValue && oldDiff && (
        <div className="border border-ui_input_border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold !text-base">
                Old Value
              </Text>
              <span className="text-xs text-ui_input_border_dark">(Full JSON)</span>
            </div>
            <button
              onClick={() => setShowOldParsedView(!showOldParsedView)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-ui_input_border hover:bg-gray-100 transition-colors"
              title={showOldParsedView ? 'Switch to Plain JSON' : 'Switch to Parsed View'}>
              <span style={{color: palette.ui_input_border_dark}}>
                {showOldParsedView ? 'Plain' : 'Parsed'}
              </span>
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto font-mono text-xs">
            {showOldParsedView ? (
              oldParsed !== null && oldParsed !== undefined ? (
                oldDiff.nodes.length > 0 ? (
                  oldDiff.nodes.map((node, index) => {
                    // Skip root node if it's a wrapper
                    if (node.key === 'root' && (isPlainObject(oldParsed) || Array.isArray(oldParsed))) {
                      return (
                        <ObjectArrayRenderer
                          key={`old.${index}`}
                          value={oldParsed}
                          path="root"
                          level={0}
                          isOldValue={true}
                          parentType={node.type}
                        />
                      );
                    }
                    return (
                      <TreeNode
                        key={`old.${index}`}
                        node={node}
                        path="root"
                        level={0}
                        isOldValue={true}
                      />
                    );
                  })
                ) : (
                  <ObjectArrayRenderer
                    value={oldParsed}
                    path="root"
                    level={0}
                    isOldValue={true}
                    parentType="unchanged"
                  />
                )
              ) : (
                <div className="p-2 text-ui_input_border">null</div>
              )
            ) : (
              <div className="bg-white rounded p-3 border border-ui_input_border">
                <pre className="whitespace-pre-wrap break-words text-xs">
                  {formatPlainJson(oldValue)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Value View */}
      <div className="border border-ui_input_border rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold !text-base">
              New Value
            </Text>
            <span className="text-xs text-ui_input_border_dark">
              ({showNewDiffView ? 'Changes Only' : 'Full JSON'})
            </span>
          </div>
          <button
            onClick={() => setShowNewDiffView(!showNewDiffView)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-ui_input_border hover:bg-gray-100 transition-colors"
            title={showNewDiffView ? 'Switch to Full JSON' : 'Switch to Diff View'}>
            <span style={{color: palette.ui_input_border_dark}}>
              {showNewDiffView ? 'Full' : 'Diff'}
            </span>
          </button>
        </div>
        <div className="max-h-[400px] overflow-y-auto font-mono text-xs">
          {showNewDiffView ? (
            // Show diff view (changes only)
            newDiff && newDiff.hasChanges ? (
              newDiff.nodes.length > 0 ? (
                newDiff.nodes.map((node, index) => {
                  // Skip root node if it's a wrapper
                  if (node.key === 'root' && (isPlainObject(newParsed) || Array.isArray(newParsed))) {
                    return (
                      <ObjectArrayRenderer
                        key={`new.${index}`}
                        value={newParsed}
                        path="root"
                        level={0}
                        isOldValue={false}
                        parentType={node.type}
                      />
                    );
                  }
                  return (
                    <TreeNode
                      key={`new.${index}`}
                      node={node}
                      path="root"
                      level={0}
                      isOldValue={false}
                    />
                  );
                })
              ) : (
                <div className="p-2 text-ui_input_border">No changes</div>
              )
            ) : (
              <div className="p-2 text-ui_input_border text-sm">No changes detected</div>
            )
          ) : (
            // Show full new value as plain JSON
            <div className="bg-white rounded p-3 border border-ui_input_border">
              <pre className="whitespace-pre-wrap break-words text-xs">
                {formatPlainJson(newValue)}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-ui_input_border_dark pt-2 border-t border-ui_input_border">
        <div className="flex items-center gap-1">
          <span className="font-mono font-bold" style={{color: palette.completed}}>+</span>
          <span>Added</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono font-bold" style={{color: palette.ui_red}}>-</span>
          <span>Removed</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono font-bold" style={{color: '#FFB800'}}>~</span>
          <span>Modified</span>
        </div>
      </div>
    </div>
  );
};
